import { CalendarCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";

const LABELS = {
  reserve: { en: "Book this stay", fr: "Réserver ce séjour", es: "Reservar" },
  reserve_short: { en: "Book", fr: "Réserver", es: "Reservar" },
};

const pick = (m, l) => (m && (m[l] || m.en)) || "";

/**
 * ReserveThisHotelButton — CTA rendered inside a hotel/room/service
 * pricing card. Adds the current hotel to the shared enquiry (if not
 * already selected) and smoothly scrolls to the on-page booking widget
 * so the visitor lands right on the pre-populated form.
 *
 * Works from any surface on the hotel detail page (RoomPriceTag popover,
 * RoomCard, ServiceCard).
 */
export default function ReserveThisHotelButton({
  hotelSlug,
  variant = "solid",
  size = "md",
  testid = "reserve-this-hotel",
  className = "",
  short = false,
}) {
  const { lang } = useLanguage();
  const { selectedHotelSlugs, toggleHotelWithFeedback } = useBooking();
  const isAdded = selectedHotelSlugs.includes(hotelSlug);

  const onClick = () => {
    if (!isAdded) toggleHotelWithFeedback(hotelSlug);
    // Smooth-scroll to the plan-your-stay booking widget. Offset accounts
    // for the fixed header (116px) + sticky sub-nav (~56px).
    const el = document.getElementById("plan-your-stay");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 170;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const base =
    size === "sm"
      ? "px-4 py-2.5 text-[10px]"
      : "px-5 py-3 text-[11px]";
  const palette =
    variant === "outline"
      ? "border border-[#2C2621]/40 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542]"
      : "bg-[#C16542] text-[#FDFBF7] border border-[#C16542] hover:bg-[#A35133] hover:border-[#A35133]";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-no-cms-text="true"
      className={`inline-flex items-center justify-center gap-2 tracking-[0.25em] uppercase transition-colors ${base} ${palette} ${className}`}
    >
      <CalendarCheck className="w-3.5 h-3.5" strokeWidth={1.7} />
      <span>{pick(short ? LABELS.reserve_short : LABELS.reserve, lang)}</span>
    </button>
  );
}
